import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TEventListItem } from "@/types/event";
import type { IWorkspaceMember } from "@/types/workspace";

type TAsyncState = "idle" | "loading" | "success" | "error";

export class EventStore {
  events: TEventListItem[] = [];
  eventsState: TAsyncState = "idle";

  /** The event currently open on the detail page; drives the breadcrumb leaf. */
  currentEvent: TEventListItem | null = null;

  workspaceMembers: IWorkspaceMember[] = [];
  membersState: TAsyncState = "idle";

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get membersById(): Record<string, IWorkspaceMember["member"]> {
    const map: Record<string, IWorkspaceMember["member"]> = {};
    for (const wm of this.workspaceMembers) {
      map[wm.member.id] = wm.member;
    }
    return map;
  }

  getMemberDisplay(memberId: string): {
    display_name: string;
    avatar_url: string;
  } {
    const member = this.membersById[memberId];
    return {
      display_name: member?.display_name ?? "Unknown",
      avatar_url: member?.avatar_url ?? "",
    };
  }

  setCurrentEvent(eventItem: TEventListItem | null): void {
    this.currentEvent = eventItem;
  }

  async fetchEvents(slug: string): Promise<void> {
    this.eventsState = "loading";
    try {
      const events = await this.rootStore.eventService.listEvents(slug);
      runInAction(() => {
        this.events = events;
        this.eventsState = "success";
      });
    } catch {
      runInAction(() => {
        this.eventsState = "error";
      });
    }
  }

  async fetchWorkspaceMembers(slug: string): Promise<void> {
    if (this.workspaceMembers.length > 0 && this.membersState === "success") {
      return;
    }
    this.membersState = "loading";
    try {
      const members = await this.rootStore.workspaceMembers.readMembers(slug);
      runInAction(() => {
        this.workspaceMembers = members;
        this.membersState = "success";
      });
    } catch {
      runInAction(() => {
        this.membersState = "error";
      });
    }
  }
}
